import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import { invalid } from '../validation.ts';
import {
  errorText,
  formatHash,
  formatError,
  hash,
  result,
  stableJson,
  validation,
  validationError,
} from './shared.ts';

function paintCanvas(canvas: HTMLCanvasElement): void {
  canvas.width = 320;
  canvas.height = 120;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D is unavailable');
  }
  const gradient = context.createLinearGradient(0, 0, 320, 120);
  gradient.addColorStop(0, '#f60');
  gradient.addColorStop(1, '#069');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 320, 120);
  context.font = '24px Arial';
  context.fillStyle = '#fff';
  context.fillText('ABC Привет 😀', 12, 48);
  context.globalCompositeOperation = 'multiply';
  context.fillStyle = 'rgba(20, 220, 120, .7)';
  context.beginPath();
  context.arc(230, 66, 42, 0, Math.PI * 2);
  context.fill();
}

async function canvasSample(): Promise<RawData> {
  const canvas = document.createElement('canvas');
  paintCanvas(canvas);
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D is unavailable');
  }
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) {
        resolve(value);
      } else {
        reject(new Error('Canvas blob creation failed'));
      }
    });
  });
  const text = context.measureText('ABC Привет 😀');
  return {
    pixelHash: await hash([...pixels]),
    dataUrlHash: await hash(canvas.toDataURL()),
    blobHash: await hash([...new Uint8Array(await blob.arrayBuffer())]),
    textMetrics: {
      width: text.width,
      ascent: text.actualBoundingBoxAscent,
      descent: text.actualBoundingBoxDescent,
    },
    emojiWidth: context.measureText('😀').width,
  };
}

async function collect(): Promise<RawData> {
  const repeats = [await canvasSample(), await canvasSample()];
  return { repeats };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return formatHash(rawData);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  const repeats = Array.isArray(rawData.repeats) ? rawData.repeats : [];
  if (repeats.length < 2 || stableJson(repeats[0]) != stableJson(repeats[1])) {
    return invalid('Canvas output changes between repeated reads');
  }
  return validation([]);
}

export const canvasRenderingTest: TestDefinition = {
  id: 'canvasRendering',
  title: 'Canvas rendering',
  category: 'shared',
  format,
  run,
  validate,
};
